
import React, { useEffect, useMemo, useState } from "react";

import {
  FaCamera,
  FaFilter,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaUserInjured,
} from "react-icons/fa";

const STATUS_OPTIONS = [
  "",
  "Licencié",
  "Licencié Hors Club",
  "Inscrit",
  "Cours d'essai",
  "Licencié - Arrêt",
  "Non Licencié - Arrêt",
  "Ancien Licencié",
  "Ancien Licencié et Arrêt",
];

const REGLEMENT_OPTIONS = [
  "",
  "Réglé",
  "Partiellement réglé",
  "En cours",
  "Non réglé",
  "Annulé",
  "Problème",
  "Inexistant",
];

export default function ListingAdherentsFilters({
  filters = {},
  setFilters,
  loading,
  dojos = [],
  groups = [],
  seasons = [],
  onRefresh,
  onReset,
}) {
  const [localSearch, setLocalSearch] = useState(
    filters.search || ""
  );

  useEffect(() => {
    setLocalSearch(filters.search || "");
  }, [filters.search]);

  useEffect(() => {
    if (localSearch === (filters.search || "")) return;

    const timer = setTimeout(() => {
      setFilters?.({ search: localSearch });
    }, 250);

    return () => clearTimeout(timer);
  }, [localSearch, filters.search, setFilters]);

  const hasSingleDojo = useMemo(
    () => dojos.length === 1,
    [dojos.length]
  );

  const fieldClass =
    "w-full min-w-0 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-400/30 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";

  const labelClass =
    "mb-1.5 block text-xs font-semibold text-slate-700 dark:text-slate-300";

  const update = (patch) => {
    setFilters?.(patch);
  };

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">

        {/* HEADER */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
              <FaFilter />
              Filtres adhérents
            </div>

            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Recherchez vos adhérents par saison, groupe, statut ou règlement.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <FaSyncAlt
                className={loading ? "animate-spin" : ""}
              />
              Actualiser
            </button>

            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <FaTimes />
              Réinitialiser
            </button>
          </div>
        </div>

        {/* GRILLE RESPONSIVE */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-12">

          {/* RECHERCHE */}
          <div className="min-w-0 sm:col-span-2 lg:col-span-4">
            <label className={labelClass}>
              Recherche
            </label>

            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={localSearch}
                onChange={(e) =>
                  setLocalSearch(e.target.value)
                }
                disabled={loading}
                placeholder="Nom, prénom, licence, groupe..."
                className={`${fieldClass} pl-9`}
              />
            </div>
          </div>

          {/* SAISON */}
          <div className="min-w-0 lg:col-span-2">
            <label className={labelClass}>
              Saison
            </label>

            <select
              value={filters.selectedSeason || ""}
              onChange={(e) =>
                update({
                  selectedSeason: e.target.value,
                  season: e.target.value,
                })
              }
              disabled={loading}
              className={fieldClass}
            >
              <option value="">
                Toutes les saisons
              </option>

              {seasons.map((season) => (
                <option key={season} value={season}>
                  {season}
                </option>
              ))}
            </select>
          </div>

          {/* DOJO */}
          {!hasSingleDojo && (
            <div className="min-w-0 lg:col-span-2">
              <label className={labelClass}>
                Dojo
              </label>

              <select
                value={filters.selectedDojo || ""}
                onChange={(e) =>
                  update({
                    selectedDojo: e.target.value,
                    dojoId: e.target.value,
                    filterGroup: "",
                    groupName: "",
                  })
                }
                disabled={loading}
                className={fieldClass}
              >
                <option value="">
                  Tous les dojos
                </option>

                {dojos.map((dojo) => (
                  <option
                    key={dojo.id}
                    value={String(dojo.id)}
                  >
                    {dojo.dojo_nom ||
                      dojo.nom ||
                      `Dojo #${dojo.id}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* GROUPE */}
          <div className="min-w-0 lg:col-span-2">
            <label className={labelClass}>
              Groupe
            </label>

            <select
              value={filters.filterGroup || ""}
              onChange={(e) =>
                update({
                  filterGroup: e.target.value,
                  groupName: e.target.value,
                })
              }
              disabled={loading}
              className={fieldClass}
            >
              <option value="">
                Tous les groupes
              </option>

              {groups.map((group) => (
                <option
                  key={group.id}
                  value={group.nom}
                >
                  {group.nom}
                </option>
              ))}
            </select>
          </div>

          {/* STATUT INSCRIPTION */}
          <div className="min-w-0 lg:col-span-2">
            <label className={labelClass}>
              Statut inscription
            </label>

            <select
              value={filters.statusFilter || ""}
              onChange={(e) =>
                update({
                  statusFilter: e.target.value,
                  statutInscription: e.target.value,
                })
              }
              disabled={loading}
              className={fieldClass}
            >
              {STATUS_OPTIONS.map((status) => (
                <option
                  key={status || "all"}
                  value={status}
                >
                  {status || "Tous les statuts"}
                </option>
              ))}
            </select>
          </div>

          {/* REGLEMENT */}
          <div className="min-w-0 lg:col-span-3">
            <label className={labelClass}>
              Règlement inscription
            </label>

            <select
              value={filters.reglementFilter || ""}
              onChange={(e) =>
                update({
                  reglementFilter: e.target.value,
                })
              }
              disabled={loading}
              className={fieldClass}
            >
              {REGLEMENT_OPTIONS.map((status) => (
                <option
                  key={status || "all"}
                  value={status}
                >
                  {status || "Tous les règlements"}
                </option>
              ))}
            </select>
          </div>

          {/* TRI */}
          <div className="min-w-0 lg:col-span-3">
            <label className={labelClass}>
              Trier par
            </label>

            <select
              value={filters.sortBy || "alpha"}
              onChange={(e) =>
                update({ sortBy: e.target.value })
              }
              disabled={loading}
              className={fieldClass}
            >
              <option value="alpha">
                Nom A → Z
              </option>

              <option value="alpha-desc">
                Nom Z → A
              </option>

              <option value="age-asc">
                Âge croissant
              </option>

              <option value="age-desc">
                Âge décroissant
              </option>

              <option value="statut">
                Par statut
              </option>

              <option value="reglement">
                Par règlement
              </option>
            </select>
          </div>
        </div>

        {/* FILTRES RAPIDES */}
        <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">

          <FilterChip
            active={!!filters.hideStopped}
            onClick={() =>
              update({
                hideStopped: !filters.hideStopped,
                hideLicencieArret: !filters.hideStopped,
              })
            }
            label="Masquer les arrêts"
          />

          <FilterChip
            active={!!filters.onlyBlessed}
            onClick={() =>
              update({
                onlyBlessed: !filters.onlyBlessed,
              })
            }
            label="Blessés / absents"
            icon={<FaUserInjured />}
            tone="red"
          />

          <FilterChip
            active={!!filters.onlyWithoutPhoto}
            onClick={() =>
              update({
                onlyWithoutPhoto:
                  !filters.onlyWithoutPhoto,
              })
            }
            label="Sans photo"
            icon={<FaCamera />}
            tone="amber"
          />
        </div>
      </div>
    </section>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  icon,
  tone = "emerald",
}) {
  const activeClass =
    tone === "red"
      ? "bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/20 dark:text-red-200 dark:ring-red-800/60"
      : tone === "amber"
      ? "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:ring-amber-800/60"
      : "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold ring-1 transition ${
        active
          ? activeClass
          : "bg-slate-100 text-slate-600 ring-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-700"
      }`}
    >
      {icon}

      <span
        className={`h-2 w-2 rounded-full ${
          active ? "bg-current" : "bg-slate-400"
        }`}
      />

      {label}
    </button>
  );
}
